import { Injectable } from '@angular/core';
import { ActivityModel, RequestParamModel } from '../models';
import { PaginationModel } from '../models';
import { BaseService } from '../services';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ActivityService {

  constructor(private baseService: BaseService) { }

  // Get Lists
  getLists(requestParamModel: RequestParamModel): any {
    return this.baseService.get('activity', requestParamModel)
      .pipe(map<any, any>(response => {
        const responseList = new PaginationModel<ActivityModel>();
        const headers = response.headers;
        responseList.setHeaders(headers);
        responseList.data = response.body.data as ActivityModel[];
        return responseList;
      }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * The History toolbar's "Done by" contents for one record, plus its
   * unfiltered entry count.
   *
   * Separate from the list because the option list has to describe the whole
   * record: built from a filtered response, picking a person would collapse the
   * dropdown to that one person. The ids carry the `b:` / `f:` prefix the list
   * filter expects — an administrator and a trade-portal contact live in
   * different tables, so an id alone would be ambiguous.
   */
  getFilterOptions(requestParamModel: RequestParamModel): any {
    return this.baseService.get('activity/filter-options', requestParamModel, true)
      .pipe(map<any, any>(response => response.body.data ?? {}),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Records how the customer email a stage offered actually settled.
   *
   * The console is the only caller, and deliberately so: of the three outcomes
   * only one has a server-side event to hang off. `skipped` is a decision taken
   * entirely here — the email preview's Skip button closes the dialog and calls
   * nothing — and the send itself goes to the v3 PHP API, which writes no
   * activity row. Reporting all three through one endpoint is what stops the
   * timeline carrying "sent" and "was not sent" from two different writers.
   *
   * Fire-and-forget at the call site, and sent with the loader AND the global
   * error toast suppressed: an audit row must never cost the user the action it
   * describes, and the row is written after the email has already settled either
   * way — a toast about it would be reporting a failure the user cannot act on
   * over the top of the one they just saw.
   *
   * @param body `{quote_order_id, outcome: 'sent'|'failed'|'skipped',
   *              email_template_title?, quote_order_status?, recipient_emails?,
   *              error?}`
   */
  recordCustomerEmail(body: any): Observable<any> {
    return this.baseService.post('activity/customer-email', body, true, true)
      .pipe(map<any, any>(response => response?.data ?? null),
        catchError(err => throwError(() => err))
      );
  }

  // get details.
  getDetails(categoryID: number, requestParamModel?: RequestParamModel): Observable<ActivityModel> {
    return this.baseService.get('activity/' + categoryID, requestParamModel)
      .pipe(map<any, ActivityModel>(response => {
        return response.body.data as ActivityModel;
      }),
        catchError(err => throwError(() => err))
      );
  }

}